/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React from 'react';
import { adminService } from '../services/adminService';

const AdminSettings = () => {
  const [settings, setSettings] = React.useState<any[]>([]);
  const [formData, setFormData] = React.useState<any>({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const fetchSettings = async () => {
    try {
      const response = await adminService.getSettings();
      setSettings(response.data);
      const initialForm: any = {};
      response.data.forEach((s: any) => {
        initialForm[s.key] = s.value;
      });
      setFormData(initialForm);
    } catch (error: any) {
      console.error('Error fetching settings', error);
      if (error.response && error.response.status === 403) {
        setError('Access Denied: Only root admins can access these settings.');
      } else {
        setError('Failed to load settings. Please ensure you are logged in as an administrator.');
      }
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchSettings();
  }, []);

  const handleChange = (key: string, value: string) => {
    setFormData({ ...formData, [key]: value });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminService.updateSettings(formData);
      alert('Settings saved successfully');
    } catch (error) {
      alert('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-8 text-center">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
        <button 
          onClick={() => window.location.href = '/'}
          className="text-indigo-600 hover:text-indigo-500 font-medium"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <h1 className="text-3xl font-bold mb-8">Platform Fee Settings</h1>
      
      <form onSubmit={handleSave} className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-indigo-700 pb-2 border-b">Personal Seller Fees</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700">Platform Fee %</label>
              <input 
                type="number" step="0.01" 
                value={formData['personal_fee_percent'] || ''} 
                onChange={(e) => handleChange('personal_fee_percent', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fixed Fee $</label>
              <input 
                type="number" step="0.01" 
                value={formData['personal_fee_fixed'] || ''} 
                onChange={(e) => handleChange('personal_fee_fixed', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              />
            </div>
          </div>

          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-indigo-700 pb-2 border-b">Pro Seller Fees</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700">Platform Fee %</label>
              <input 
                type="number" step="0.01" 
                value={formData['pro_fee_percent'] || ''} 
                onChange={(e) => handleChange('pro_fee_percent', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fixed Fee $</label>
              <input 
                type="number" step="0.01" 
                value={formData['pro_fee_fixed'] || ''} 
                onChange={(e) => handleChange('pro_fee_fixed', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Monthly Subscription $</label>
              <input 
                type="number" step="0.01" 
                value={formData['pro_monthly_subscription'] || ''} 
                onChange={(e) => handleChange('pro_monthly_subscription', e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
              />
            </div>
          </div>
        </div>

        <div className="mt-10 pt-6 border-t flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="bg-indigo-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-md"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSettings;
